import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import * as Yup from 'yup';
import { handleForgotPasswordReset } from '../../common/auth/AuthService';
import {
  Stack,
  TextField,
  Typography,
  Button,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  FormHelperText,
  Input,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const validationSchema = Yup.object().shape({
  newPassword: Yup.string()
    .min(8, 'Password must be at least 8 characters.')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\^$*.[\]{}()?\-“!@#%&/,><’:;|_~`])\S{8,99}$/,
      'Password requires an upper and lower case letter, a number, and at least one of the following special characters (no other special characters allowed): ^ $ * . [ ] { } ( ) ? " ! @ # % & /  , > < \' : ; | _ ~ `'
    )
    .required('Required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Passwords do not match')
    .required('Required'),
  authenticationCode: Yup.string().required('Required'),
  username: Yup.string().email('Invalid Email').required('Required'),
});

export default function SetNewPasswordForm(props) {
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const username = props.username;

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return (
    <Formik
      initialValues={{
        username: username,
        authenticationCode: '',
        newPassword: '',
        confirmPassword: '',
      }}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting }) => {
        handleForgotPasswordReset(values)
          .then(() => {
            props.successAction();
          })
          .catch((err) => {
            console.error(err);
            setErrorMessage(err.message);
            setSubmitting(false);
          });
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        isSubmitting,
      }) => (
        <Form>
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='center'
            spacing={2}
          >
            <TextField
              name='username'
              type='email'
              variant='standard'
              value={values.username}
              disabled={true}
              error={errors.username && touched.username}
              helperText={touched.username && errors.username}
              sx={{ m: 1, width: '20vw', minWidth: '35ch' }}
              inputProps={{ maxLength: 256 }}
            />
            <TextField
              name='authenticationCode'
              type='text'
              label='Verification Code'
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.authenticationCode}
              placeholder='Verification Code'
              variant='standard'
              error={errors.authenticationCode && touched.authenticationCode}
              helperText={
                touched.authenticationCode && errors.authenticationCode
              }
              sx={{ m: 1, width: '20vw', minWidth: '35ch' }}
              inputProps={{ maxLength: 10 }}
            />
            <FormControl
              sx={{ m: 1, width: '20vw', minWidth: '35ch' }}
              variant='standard'
            >
              <InputLabel htmlFor='standard-adornment-password'>
                New Password
              </InputLabel>
              <Input
                name='newPassword'
                type={showPassword ? 'text' : 'password'}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.newPassword}
                placeholder='New Password'
                variant='standard'
                error={errors.newPassword && touched.newPassword}
                endAdornment={
                  <InputAdornment position='end'>
                    <IconButton
                      sx={{ m: 0 }}
                      aria-label='toggle password visibility'
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge='end'
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                inputProps={{ maxLength: 64 }}
              />
              <FormHelperText id='new-password-error-message' error>
                {touched.newPassword && errors.newPassword}
              </FormHelperText>
            </FormControl>
            <FormControl
              sx={{ m: 1, width: '20vw', minWidth: '35ch' }}
              variant='standard'
            >
              <InputLabel htmlFor='standard-adornment-password'>
                Confirm Password
              </InputLabel>
              <Input
                name='confirmPassword'
                type={showPassword ? 'text' : 'password'}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.password}
                placeholder='Confirm Password'
                variant='standard'
                error={errors.confirmPassword && touched.confirmPassword}
                endAdornment={
                  <InputAdornment position='end'>
                    <IconButton
                      sx={{ m: 0 }}
                      aria-label='toggle password visibility'
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge='end'
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                inputProps={{ maxLength: 64 }}
              />
              <FormHelperText id='confirm-password-error-message' error>
                {touched.confirmPassword && errors.confirmPassword}
              </FormHelperText>
            </FormControl>
            {errorMessage && (
              <Typography sx={{ color: 'error.main' }}>
                {errorMessage}
              </Typography>
            )}
            <Box sx={{ m: 1, position: 'relative' }}>
              <Button
                sx={{ marginTop: 1 }}
                type={'submit'}
                disabled={isSubmitting}
                variant='contained'
              >
                Submit New Password
              </Button>
              {isSubmitting && (
                <CircularProgress
                  size={24}
                  sx={{
                    color: 'secondary.main',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-12px',
                    marginLeft: '-12px',
                  }}
                />
              )}
            </Box>
          </Stack>
        </Form>
      )}
    </Formik>
  );
}
